/**
 * Owner: Masa Bayu
 * File: global-taxonomy.ts
 * Date:Time: 2026-09-18 12:15
 * Description: Global taxonomy API — canonical IDs · locale labels · classification (≠ T2 UI).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertOptionalTrimmed, assertValidId } from './guards';
import { QXREAD_TIER1_TOPIC_SEED } from './global-taxonomy-seed';
export { QXREAD_TAXONOMY_DIMENSIONS, QXREAD_TOPIC_STATUSES, } from './global-taxonomy-types';
export { QXREAD_TIER1_TOPIC_SEED } from './global-taxonomy-seed';
/** Curated Home tiles — quiet UI (≠ full Tier-1 dump). */
export const QXREAD_HOME_TOPIC_TILE_IDS = Object.freeze([
    'mathematics_science',
    'engineering_technology',
    'business_economics',
    'arts_culture',
    'history_civilization',
    'literature_fiction',
    'education',
    'lifestyle',
    'philosophy_thought',
]);
/** Dotted canonical topic id: segment.segment… (a-z0-9_). */
export const QXREAD_CANONICAL_TOPIC_ID_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/;
const FALLBACK_LOCALE = 'en';
export function assertCanonicalTopicId(topicId, label = 'QXRead topicId') {
    assertValidId(topicId, label);
    if (!QXREAD_CANONICAL_TOPIC_ID_PATTERN.test(topicId)) {
        throw new Error(`${label} must be a dotted canonical id (e.g. mathematics_science.physics.quantum)`);
    }
}
export function createQxreadTopicNode(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead topic node');
    assertCanonicalTopicId(options.topicId);
    if (!Number.isInteger(options.tier) || options.tier < 1) {
        throw new Error('QXRead topic tier must be an integer >= 1');
    }
    if (options.parentId != null) {
        assertCanonicalTopicId(options.parentId, 'QXRead topic parentId');
    }
    const labels = freezeLabels(options.labels, 'QXRead topic labels');
    if (labels[FALLBACK_LOCALE] === undefined && Object.keys(labels).length === 0) {
        throw new Error('QXRead topic labels must include at least one locale');
    }
    return Object.freeze({
        topicId: options.topicId,
        parentId: options.parentId ?? null,
        tier: options.tier,
        status: options.status,
        labels,
    });
}
/** Resolve display label; prefer locale, then en, then first available. */
export function resolveTopicLabel(node, locale = FALLBACK_LOCALE) {
    const direct = node.labels[locale];
    if (direct !== undefined && direct.length > 0) {
        return direct;
    }
    const en = node.labels[FALLBACK_LOCALE];
    if (en !== undefined && en.length > 0) {
        return en;
    }
    const first = Object.values(node.labels)[0];
    if (first !== undefined && first.length > 0) {
        return first;
    }
    return node.topicId;
}
export function listTier1Topics(seed = QXREAD_TIER1_TOPIC_SEED) {
    return Object.freeze(seed.filter((node) => node.tier === 1 && node.parentId === null));
}
/** Home Explore Topics — curated subset of Tier-1 (never dump all CORE topics). */
export function listHomeTopicTiles(locale = FALLBACK_LOCALE, seed = QXREAD_TIER1_TOPIC_SEED) {
    const byId = new Map(seed.map((node) => [node.topicId, node]));
    return Object.freeze(QXREAD_HOME_TOPIC_TILE_IDS.flatMap((topicId) => {
        const node = byId.get(topicId);
        if (node === undefined) {
            return [];
        }
        return [{ topicId, label: resolveTopicLabel(node, locale) }];
    }));
}
export function findTopicById(topicId, catalog = QXREAD_TIER1_TOPIC_SEED) {
    assertCanonicalTopicId(topicId);
    for (const node of catalog) {
        if (node.topicId === topicId) {
            return node;
        }
    }
    return null;
}
export function createQxreadPublicationClassification(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead publication classification');
    assertCanonicalTopicId(options.primaryTopicId, 'QXRead primaryTopicId');
    const secondaryTopicIds = freezeIdList(options.secondaryTopicIds, 'secondaryTopicIds');
    const subtopicIds = freezeIdList(options.subtopicIds, 'subtopicIds');
    const genreIds = freezeIdList(options.genreIds, 'genreIds', false);
    const audienceIds = freezeIdList(options.audienceIds, 'audienceIds', false);
    const regionIds = freezeIdList(options.regionIds, 'regionIds', false);
    const keywords = Object.freeze((options.keywords ?? []).map((kw, index) => {
        const trimmed = assertOptionalTrimmed(kw, `QXRead keywords[${String(index)}]`);
        if (trimmed === null) {
            throw new Error(`QXRead keywords[${String(index)}] must be non-empty`);
        }
        return trimmed;
    }));
    let publicationTypeId = null;
    if (options.publicationTypeId != null && options.publicationTypeId.length > 0) {
        assertValidId(options.publicationTypeId, 'QXRead publicationTypeId');
        publicationTypeId = options.publicationTypeId;
    }
    let language = null;
    if (options.language != null && options.language.length > 0) {
        language = assertOptionalTrimmed(options.language, 'QXRead language') ?? null;
    }
    return Object.freeze({
        primaryTopicId: options.primaryTopicId,
        secondaryTopicIds,
        subtopicIds,
        keywords,
        genreIds,
        publicationTypeId,
        audienceIds,
        language,
        regionIds,
    });
}
/** Classification never implies ownership, entitlement, or Reader launch. */
export function isClassificationPresentationOnly(_classification) {
    return true;
}
/** Minimal CORE genre seed — delivery form, not subject matter. */
export const QXREAD_CORE_GENRE_SEED = Object.freeze([
    genre('fiction', 'Fiction'),
    genre('non_fiction', 'Non-fiction'),
    genre('biography', 'Biography'),
    genre('essay', 'Essay'),
    genre('textbook', 'Textbook'),
    genre('guide', 'Guide'),
    genre('research', 'Research'),
    genre('memoir', 'Memoir'),
    genre('romance', 'Romance'),
    genre('thriller', 'Thriller'),
]);
/** Minimal CORE publication-type seed. */
export const QXREAD_CORE_PUBLICATION_TYPE_SEED = Object.freeze([
    pubType('ebook', 'eBook'),
    pubType('magazine', 'Magazine'),
    pubType('journal', 'Journal'),
    pubType('textbook', 'Textbook'),
    pubType('report', 'Report'),
    pubType('comic', 'Comic'),
    pubType('manual', 'Manual'),
    pubType('catalogue', 'Catalogue'),
    pubType('newsletter', 'Newsletter'),
]);
/** Minimal CORE audience seed. */
export const QXREAD_CORE_AUDIENCE_SEED = Object.freeze([
    audience('children', 'Children'),
    audience('young_adult', 'Young Adult'),
    audience('general', 'General'),
    audience('professional', 'Professional'),
    audience('academic', 'Academic'),
    audience('researcher', 'Researcher'),
]);
function freezeLabels(labels, label) {
    const out = {};
    for (const [locale, value] of Object.entries(labels)) {
        const trimmedLocale = assertOptionalTrimmed(locale, `${label} locale`);
        const trimmedValue = assertOptionalTrimmed(value, `${label}[${locale}]`);
        if (trimmedLocale === null || trimmedValue === null) {
            throw new Error(`${label} entries must be non-empty`);
        }
        out[trimmedLocale] = trimmedValue;
    }
    return Object.freeze(out);
}
function freezeIdList(ids, field, canonical = true) {
    return Object.freeze((ids ?? []).map((id, index) => {
        if (canonical) {
            assertCanonicalTopicId(id, `QXRead ${field}[${String(index)}]`);
        }
        else {
            assertValidId(id, `QXRead ${field}[${String(index)}]`);
        }
        return id;
    }));
}
function genre(genreId, en) {
    return Object.freeze({ genreId, labels: Object.freeze({ en }) });
}
function pubType(publicationTypeId, en) {
    return Object.freeze({ publicationTypeId, labels: Object.freeze({ en }) });
}
function audience(audienceId, en) {
    return Object.freeze({ audienceId, labels: Object.freeze({ en }) });
}
//# sourceMappingURL=global-taxonomy.js.map